import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: { 'Content-Type': 'application/json' },
});

// Attach Bearer token on every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// On 401 clear storage and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ─────────────────────────────────────────────────────────────────────
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const googleAuth = (data) => api.post('/auth/google', data);

// ── Products ──────────────────────────────────────────────────────────────────
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (formData) =>
  api.post('/products', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateProduct = (id, formData) =>
  api.put(`/products/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// ── Categories ────────────────────────────────────────────────────────────────
export const getCategories = (params) => api.get('/categories', { params });
export const getCategory = (id) => api.get(`/categories/${id}`);
export const createCategory = (formData) =>
  api.post('/categories', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateCategory = (id, formData) =>
  api.put(`/categories/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// ── Vendors ───────────────────────────────────────────────────────────────────
export const getVendors = (params) => api.get('/vendors', { params });
export const getVendor = (id) => api.get(`/vendors/${id}`);
export const applyVendor = (formData) =>
  api.post('/vendors/apply', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getVendorStats = () => api.get('/vendors/dashboard/stats');
export const updateVendorProfile = (formData) =>
  api.put('/vendors/profile', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateVendorStatus = (id, data) => api.put(`/vendors/${id}/status`, data);

// ── Orders ────────────────────────────────────────────────────────────────────
export const placeOrder = (data) => api.post('/orders', data);
export const getMyOrders = (params) => api.get('/orders/my', { params });
export const getVendorOrders = (params) => api.get('/orders/vendor/all', { params });
export const getOrder = (id) => api.get(`/orders/${id}`);
export const updateOrderStatus = (id, data) => api.put(`/orders/${id}/status`, data);

// ── Reviews ───────────────────────────────────────────────────────────────────
export const getProductReviews = (productId, params) =>
  api.get(`/reviews/product/${productId}`, { params });
export const addReview = (data) => api.post('/reviews', data);
export const deleteReview = (id) => api.delete(`/reviews/${id}`);

// ── User profile ──────────────────────────────────────────────────────────────
export const updateProfile = (formData) =>
  api.put('/users/profile', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// ── Admin ─────────────────────────────────────────────────────────────────────
export const getAllOrders = (params) => api.get('/admin/orders', { params });
export const getUsers = (params) => api.get('/users', { params });
export const getAdminDashboard = () => api.get('/admin/dashboard');
export const getAdminUsers = (params) => api.get('/admin/users', { params });
export const updateUserStatus = (id, data) => api.put(`/admin/users/${id}/status`, data);
export const updateUserRole = (id, data) => api.put(`/admin/users/${id}/role`, data);
export const getAdminSettings = () => api.get('/admin/settings');
export const updateAdminSettings = (data) => api.put('/admin/settings', data);
export const getPublicSettings = () => api.get('/settings/public');
export const createAdminCategory = (formData) =>
  api.post('/admin/categories', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateAdminCategory = (id, formData) =>
  api.put(`/admin/categories/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteAdminCategory = (id) => api.delete(`/admin/categories/${id}`);
export const getAdminReturns = (params) => api.get('/returns', { params });
export const updateReturnStatus = (id, data) => api.put(`/returns/${id}/status`, data);
export const getAdminWithdrawals = (params) => api.get('/admin/withdrawals', { params });
export const processWithdrawal = (id, data) => api.put(`/admin/withdrawals/${id}`, data);

// ── Cart helpers ──────────────────────────────────────────────────────────────
export const syncCart = (items) => api.put('/users/cart', { items });

// ── Wishlist ──────────────────────────────────────────────────────────────────
export const toggleWishlist = (productId) => api.put(`/users/wishlist/${productId}`);
export const getWishlist = () => api.get('/users/wishlist');

// ── Notifications ─────────────────────────────────────────────────────────────
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);

// ── Wallet ────────────────────────────────────────────────────────────────────
export const getWallet = () => api.get('/wallet');
export const submitWithdrawal = (data) => api.post('/wallet/withdraw', data);
export const getWithdrawals = () => api.get('/wallet/withdrawals');

// ── Returns ───────────────────────────────────────────────────────────────────
export const getMyReturns = (params) => api.get('/returns/my', { params });
export const submitReturn = (data) => api.post('/returns', data);
export const getVendorReturns = (params) => api.get('/returns/vendor', { params });

// ── Analytics ─────────────────────────────────────────────────────────────────
export const getVendorSalesAnalytics = (params) => api.get('/analytics/vendor/sales', { params });
export const getVendorProductsAnalytics = () => api.get('/analytics/vendor/products');
export const getVendorCustomersAnalytics = () => api.get('/analytics/vendor/customers');
export const getAdminAnalyticsOverview = (params) => api.get('/analytics/admin/overview', { params });
export const getAdminRevenueAnalytics = (params) => api.get('/analytics/admin/revenue', { params });
export const getTopVendors = () => api.get('/analytics/admin/top-vendors');
export const getTopProducts = () => api.get('/analytics/admin/top-products');

// ── Search ────────────────────────────────────────────────────────────────────
export const searchAll = (params) => api.get('/search', { params });
export const getSearchSuggestions = (params) => api.get('/search/suggestions', { params });
export const getTrendingProducts = () => api.get('/products/trending');
export const getRelatedProducts = (id) => api.get(`/products/${id}/related`);
export const getRecommendations = () => api.get('/recommendations');

// ── Shipping ──────────────────────────────────────────────────────────────────
export const calculateShipping = (data) => api.post('/shipping/calculate', data);
export const getShippingZones = () => api.get('/shipping/zones');
export const createShippingZone = (data) => api.post('/shipping/zones', data);
export const updateShippingZone = (id, data) => api.put(`/shipping/zones/${id}`, data);
export const deleteShippingZone = (id) => api.delete(`/shipping/zones/${id}`);

// ── Messaging ─────────────────────────────────────────────────────────────────
export const startConversation = (data) => api.post('/messages/start', data);
export const getConversations = () => api.get('/messages/conversations');
export const getMessages = (conversationId, params) => api.get(`/messages/${conversationId}`, { params });
export const sendMessage = (conversationId, data) => api.post(`/messages/${conversationId}`, data);

// ── Product Q&A ───────────────────────────────────────────────────────────────
export const getProductQA = (productId, params) => api.get(`/products/${productId}/qa`, { params });
export const askQuestion = (productId, data) => api.post(`/products/${productId}/qa`, data);
export const answerQuestion = (id, data) => api.put(`/qa/${id}/answer`, data);

// ── Order Tracking ────────────────────────────────────────────────────────────
export const addTrackingUpdate = (orderId, data) => api.put(`/orders/${orderId}/tracking`, data);
export const getTrackingInfo = (orderId, params) => api.get(`/orders/${orderId}/tracking`, { params });

// ── Recently Viewed ───────────────────────────────────────────────────────────
export const trackRecentlyViewed = (productId) => api.put(`/users/recently-viewed/${productId}`);
export const getRecentlyViewed = () => api.get('/users/recently-viewed');

// ── Coupons ───────────────────────────────────────────────────────────────────
export const validateCoupon = (data) => api.post('/coupons/validate', data);

export default api;
